// Collision (§18.3, §2.10): bodies against map solids (respecting `top`), soft body–body
// separation with restitution impulses and contact credit, and the dynamic props (cubes,
// dummies). Predict mode (§18.2) corrects only the local bean: remote beans and props are
// immovable, and contact credit and mode touches are server-only.
import { MAX_BEANS, TUNE } from '../constants';
import { BS, Cause, EK } from '../ids';
import { MAPS } from '../maps/index';
import { SolidKind, type Solid } from '../maps/mapTypes';
import { MODES } from '../modes/index';
import { closestOnSegment, v2 } from '../math';
import type { Bean, Entity, Vec2, World } from '../types';
import { hasBody } from './bean';
import { applyKnockback, canBeHit } from './combat';
import { dcos, dsin } from './dmath';
import { isProp, propHidden, propMass, propRadius } from './props';
import { isPredicting, isPuppet, isTeammate, setTouch } from './world';

const solids: Solid[] = [];
const contact: Vec2 = v2();
const normal: Vec2 = v2();

/** Beans that collide with solids, props and each other: bodies free on (or above) the arena floor. */
export function bodyCollides(b: Bean): boolean {
  if (!hasBody(b)) return false;
  switch (b.state) {
    case BS.LEDGE: case BS.CLIMB: case BS.TEETER: case BS.GRABBED: case BS.SPUN: case BS.FROZEN:
      return false;
    default:
      return true;
  }
}

/** Beans that take part in body–body contact; teetering beans are anchored and immovable. */
function inContact(b: Bean): boolean {
  return bodyCollides(b) || (b.state === BS.TEETER && b.active && !b.out);
}

/**
 * A lunge passes through beans and props (§2.5: it is identical whether or not it hits), so it
 * neither carries its victim along nor stops on a body; solids still block it. This also keeps the
 * local lunge exact under prediction, where remote bodies are immovable.
 */
function passesThrough(b: Bean): boolean {
  return b.state === BS.LUNGE;
}

/**
 * Inverse mass in bean–bean contact. Remote puppets keep their mass in predict mode, so the local
 * bean's contacts split exactly as on the server instead of the local bean taking 100% (§18.2).
 * WP3 change, see docs/decisions/WP3.md: with immovable puppets every push between the local bean
 * and a remote one was a misprediction, and the §30.5 soak measured p95 0.31 m against its 0.25 m bar.
 */
function beanInvMass(_w: World, b: Bean): number {
  return b.state === BS.TEETER ? 0 : 1 / b.mass;
}

function propInvMass(w: World, e: Entity): number {
  return isPredicting(w) ? 0 : 1 / propMass(e);
}

/**
 * Pushes a circle (pos, vel, z, r) out of solid `s` and reflects its approaching normal velocity
 * relative to the solid's surface with the solid's restitution. Writes the push normal into
 * `normal` and returns true on contact. Bodies at or above `top − topSlack` pass over.
 */
function circleVsSolid(pos: Vec2, vel: Vec2, z: number, r: number, s: Solid): boolean {
  if (z >= s.top - TUNE.collide.topSlack) return false;
  if (s.kind === SolidKind.CIRCLE) {
    contact.x = s.ax;
    contact.y = s.ay;
  } else {
    closestOnSegment(pos.x, pos.y, s.ax, s.ay, s.bx, s.by, contact);
  }
  const dx = pos.x - contact.x;
  const dy = pos.y - contact.y;
  const d = Math.hypot(dx, dy);
  const pen = r + s.r - d;
  if (pen <= 0) return false;
  if (d > 0) {
    normal.x = dx / d;
    normal.y = dy / d;
  } else if (s.kind === SolidKind.CAPSULE) {
    const l = Math.hypot(s.bx - s.ax, s.by - s.ay) || 1;
    normal.x = -(s.by - s.ay) / l;
    normal.y = (s.bx - s.ax) / l;
  } else {
    normal.x = 1;
    normal.y = 0;
  }
  pos.x += normal.x * pen;
  pos.y += normal.y * pen;
  // Surface velocity at the contact point: linear plus rotation about (cx, cy).
  const px = contact.x + normal.x * s.r;
  const py = contact.y + normal.y * s.r;
  const svx = s.vx - s.omega * (py - s.cy);
  const svy = s.vy + s.omega * (px - s.cx);
  const rel = (vel.x - svx) * normal.x + (vel.y - svy) * normal.y;
  if (rel < 0) {
    const j = (1 + s.restitution) * rel;
    vel.x -= j * normal.x;
    vel.y -= j * normal.y;
  }
  return true;
}

/**
 * Soft separation of two overlapping circles (§2.10): the overlap × stiffness is split by inverse
 * mass, each share capped per tick; approaching bodies get a restitution impulse along the normal.
 * (nx, ny) points from A to B.
 */
function separate(pa: Vec2, va: Vec2, ia: number, pb: Vec2, vb: Vec2, ib: number, overlap: number, nx: number, ny: number): void {
  const sum = ia + ib;
  if (sum === 0) return;
  const corr = overlap * TUNE.body.stiffness;
  const ca = Math.min((corr * ia) / sum, TUNE.body.maxCorrection);
  const cb = Math.min((corr * ib) / sum, TUNE.body.maxCorrection);
  pa.x -= nx * ca;
  pa.y -= ny * ca;
  pb.x += nx * cb;
  pb.y += ny * cb;
  const rel = (vb.x - va.x) * nx + (vb.y - va.y) * ny;
  if (rel < 0) {
    const j = (-(1 + TUNE.body.restitution) * rel) / sum;
    va.x -= nx * j * ia;
    va.y -= ny * j * ia;
    vb.x += nx * j * ib;
    vb.y += ny * j * ib;
  }
}

function beansVsSolids(w: World, n: number): void {
  const map = MAPS[w.cfg.mapId];
  for (const b of w.beans) {
    if (!bodyCollides(b) || isPuppet(w, b.slot)) continue;
    for (let i = 0; i < n; i++) {
      const s = solids[i];
      if (circleVsSolid(b.pos, b.vel, b.z, b.radius, s)) map.onSolidContact(w, b.slot, false, s, normal.x, normal.y);
    }
  }
}

/**
 * Contact credit (§2.10, server-only): a pusher moving toward the victim (unit normal (nx, ny)) with
 * a closing speed of at least `creditMinSpeed` claims it with cause BODY. A credit the pusher
 * already holds (its shove's follow-through) keeps its cause and only refreshes its tick.
 */
function contactCredit(w: World, pusher: Bean, victim: Bean, nx: number, ny: number, closing: number): void {
  if (closing < TUNE.body.creditMinSpeed || pusher.vel.x * nx + pusher.vel.y * ny <= 0 || isTeammate(w, pusher.slot, victim.slot)) return;
  if (victim.touchBy === pusher.slot && w.tick - victim.touchTick <= TUNE.credit.windowTicks) victim.touchTick = w.tick;
  else setTouch(w, victim.slot, pusher.slot, Cause.BODY);
}

function beansVsBeans(w: World): void {
  const server = !isPredicting(w);
  const mode = MODES[w.cfg.modeId];
  for (let i = 0; i < MAX_BEANS; i++) {
    const a = w.beans[i];
    if (!inContact(a)) continue;
    for (let j = i + 1; j < MAX_BEANS; j++) {
      const b = w.beans[j];
      if (!inContact(b) || Math.abs(a.z - b.z) > TUNE.hitDzDefault) continue;
      const dx = b.pos.x - a.pos.x;
      const dy = b.pos.y - a.pos.y;
      const d = Math.hypot(dx, dy);
      const reach = a.radius + b.radius;
      if (server && d <= reach + TUNE.potato.touchSlack) mode.onTouch(w, i, j);
      if (d >= reach || passesThrough(a) || passesThrough(b)) continue;
      const nx = d > 0 ? dx / d : 1;
      const ny = d > 0 ? dy / d : 0;
      if (server) {
        const closing = (a.vel.x - b.vel.x) * nx + (a.vel.y - b.vel.y) * ny;
        contactCredit(w, a, b, nx, ny, closing);
        contactCredit(w, b, a, -nx, -ny, closing);
      }
      separate(a.pos, a.vel, beanInvMass(w, a), b.pos, b.vel, beanInvMass(w, b), reach - d, nx, ny);
    }
  }
}

/** Cubes block bodies below their top; dummies, like beans, within the vertical contact tolerance. */
function propTouchesBean(e: Entity, b: Bean): boolean {
  return e.kind === EK.CUBE ? b.z < TUNE.cube.top - TUNE.collide.topSlack : Math.abs(b.z - e.z) <= TUNE.hitDzDefault;
}

function propsVsBeans(w: World): void {
  for (const e of w.ents) {
    if (!isProp(e) || propHidden(e)) continue;
    const re = propRadius(e);
    const ie = propInvMass(w, e);
    for (const b of w.beans) {
      if (!inContact(b) || passesThrough(b) || !propTouchesBean(e, b)) continue;
      const dx = e.pos.x - b.pos.x;
      const dy = e.pos.y - b.pos.y;
      const d = Math.hypot(dx, dy);
      const reach = b.radius + re;
      if (d >= reach) continue;
      separate(b.pos, b.vel, beanInvMass(w, b), e.pos, e.vel, ie, reach - d, d > 0 ? dx / d : 1, d > 0 ? dy / d : 0);
    }
  }
}

function propsVsWorld(w: World, n: number): void {
  const map = MAPS[w.cfg.mapId];
  const ents = w.ents;
  for (let i = 0; i < ents.length; i++) {
    const e = ents[i];
    if (!isProp(e) || propHidden(e)) continue;
    const re = propRadius(e);
    for (let k = 0; k < n; k++) {
      const s = solids[k];
      if (circleVsSolid(e.pos, e.vel, e.z, re, s)) map.onSolidContact(w, e.id, true, s, normal.x, normal.y);
    }
    for (let j = i + 1; j < ents.length; j++) {
      const f = ents[j];
      if (!isProp(f) || propHidden(f) || Math.abs(e.z - f.z) > TUNE.hitDzDefault) continue;
      const dx = f.pos.x - e.pos.x;
      const dy = f.pos.y - e.pos.y;
      const d = Math.hypot(dx, dy);
      const reach = re + propRadius(f);
      if (d >= reach) continue;
      separate(e.pos, e.vel, 1 / propMass(e), f.pos, f.vel, 1 / propMass(f), reach - d, d > 0 ? dx / d : 1, d > 0 ? dy / d : 0);
    }
  }
}

/** Step phase 7: solids, body–body contact, props. */
export function resolveCollisions(w: World): void {
  const n = MAPS[w.cfg.mapId].solids(w, solids);
  beansVsSolids(w, n);
  beansVsBeans(w);
  propsVsBeans(w);
  if (!isPredicting(w)) propsVsWorld(w, n);
}

/**
 * Radial nudge of every hittable bean within `radius` (and below `maxZ`) away from (x, y) at
 * `speed`, with Dizzy and touch credit, never tumbling; teetering, hanging and climbing beans
 * fall (§2.12 pebbles, §6.5 bubbles). A ghost's pebble on its own teammate follows the Teams rule
 * (half the nudge, no Dizzy, no credit). Returns a bitmask of the slots nudged. Server-only.
 */
export function nudgeBodiesMask(w: World, x: number, y: number, radius: number, speed: number, dizzy: number, cause: Cause, by: number, maxZ: number): number {
  if (isPredicting(w)) return 0;
  let mask = 0;
  for (const b of w.beans) {
    if (!canBeHit(b) || b.z >= maxZ) continue;
    const dx = b.pos.x - x;
    const dy = b.pos.y - y;
    const d = Math.hypot(dx, dy);
    if (d > radius) continue;
    applyKnockback(w, b.slot, d > 0 ? dx : -dcos(b.facing), d > 0 ? dy : -dsin(b.facing), speed,
      { dizzy, cause, by, scaleByDizzy: false, noTumble: true, teammateCheck: true });
    mask |= 1 << b.slot;
  }
  return mask;
}

/** Pebbles and bubbles: radial nudge without tumble; teetering/hanging/climbing bodies fall. */
export function nudgeBodiesInRadius(w: World, x: number, y: number, radius: number, speed: number, dizzy: number, cause: Cause, by: number, maxZ: number): void {
  nudgeBodiesMask(w, x, y, radius, speed, dizzy, cause, by, maxZ);
}
